/**
 * Sprint-execution digest context — a pure, React-free projection of the
 * workspace's sprint activity into a compact, bounded context block for the AI
 * Team Digest.
 *
 * It reasons over the same entities the rest of the intelligence layer already
 * loads (sprints, tasks, per-task standup updates — see {@link EntitySnapshot})
 * and derives, for the reporting window:
 *
 *   • SPRINT PROGRESS  — for each active sprint: Day X/Y, goal, committed /
 *     completed / remaining effort, % complete, and the TODO / IN PROGRESS /
 *     DONE status distribution.
 *   • RECENT STATUS CHANGES — active-sprint tasks written inside the window,
 *     with their current lane (an approximation: the store keeps only the
 *     latest state + `updatedAt`, not a per-transition history).
 *   • SCOPE CHANGES — tasks flagged ad-hoc / unplanned (pulled into a sprint
 *     mid-flight), attributing the added effort. (Task REMOVAL is not persisted
 *     as history, so it is intentionally NOT fabricated here.)
 *   • STANDUP HIGHLIGHTS — task updates created inside the window (completed /
 *     next / blocker), the Daily-Scrum source.
 *
 * All numbers are computed with the SAME deterministic engines the Sprint Board
 * uses ({@link sprintProgress}, {@link sprintDayInfo}, {@link unplannedScope}),
 * so the digest's figures always agree with the board. Every section is bounded
 * per item + in count so the prompt stays small regardless of team size.
 *
 * Pure TS — no React, no I/O.
 */

import type { Task } from '../tasks/types';
import type { Sprint, TaskUpdate } from '../sprintboard/models/types';
import type { TimeWindow } from './types';
import { sprintProgress } from '../sprintboard/engine/aggregation';
import { sprintDayInfo } from '../sprintboard/models/sprintboardModel';
import { unplannedScope } from '../tasks/taskModel';

/** Max items emitted per section (keeps the prompt bounded). */
const PER_SECTION_LIMIT = 12;
/** Max characters of free text per item. */
const MAX_CHARS = 180;

/** Trims + collapses whitespace, then clamps to {@link MAX_CHARS}. */
function clamp(text: string | undefined): string {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  return t.length > MAX_CHARS ? `${t.slice(0, MAX_CHARS)}…` : t;
}

/** True when an ISO/date string parses to an instant inside [from, to]. */
function inWindow(iso: string | undefined, fromMs: number, toMs: number): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  return t >= fromMs && t <= toMs;
}

function isDone(task: Task): boolean {
  return (task.boardStatus ?? 'TODO') === 'DONE';
}

/**
 * Builds the compact "SPRINT EXECUTION" context block for the digest, or `''`
 * when there is nothing to report (no active sprint, no in-window scope change,
 * and no in-window standup update). The caller appends the returned string to
 * the digest CONTEXT only when non-empty.
 *
 * @param sprints     all workspace sprints (from the snapshot)
 * @param tasks       all workspace tasks (from the snapshot)
 * @param taskUpdates all per-task standup updates (from the snapshot)
 * @param window      the digest reporting window
 * @param now         injectable clock (for deterministic Day X/Y + tests)
 */
export function buildSprintExecutionContext(
  sprints: Sprint[],
  tasks: Task[],
  taskUpdates: TaskUpdate[],
  window: TimeWindow,
  now: Date = new Date(),
): string {
  const fromMs = Date.parse(window.fromIso);
  const toMs = Date.parse(window.toIso);
  const allTasks = tasks ?? [];
  const titleById = new Map(allTasks.map((t) => [t.id, t.title]));

  const activeSprints = (sprints ?? []).filter((s) => s.status === 'active');
  const activeSprintIds = new Set(activeSprints.map((s) => s.id));

  // Tasks belonging to any active sprint.
  const activeSprintTasks = allTasks.filter(
    (t) => t.sprintId && activeSprintIds.has(t.sprintId),
  );

  const sections: string[] = [];

  // --- Sprint progress (one entry per active sprint) -----------------------
  const progressLines: string[] = [];
  for (const s of activeSprints.slice(0, PER_SECTION_LIMIT)) {
    const sprintTasks = activeSprintTasks.filter((t) => t.sprintId === s.id);
    const prog = sprintProgress(sprintTasks);
    const day = sprintDayInfo(s.startDate, s.endDate, now);
    const dist = { TODO: 0, IN_PROGRESS: 0, DONE: 0 };
    for (const t of sprintTasks) {
      const lane = (t.boardStatus ?? 'TODO') as keyof typeof dist;
      dist[lane] = (dist[lane] ?? 0) + 1;
    }
    const blockedCount = sprintTasks.filter((t) => t.blocked && !isDone(t)).length;
    const remaining = Math.round((prog.committedDays - prog.completedDays) * 100) / 100;
    const dayLabel = day.total > 0 ? `Day ${day.day}/${day.total}` : 'unscheduled';
    const goal = s.goal ? ` — goal: ${clamp(s.goal)}` : '';
    progressLines.push(
      `- ${s.name} (${dayLabel})${goal}: ${prog.percentComplete}% complete · ` +
        `effort ${prog.completedDays}/${prog.committedDays}d (remaining ${remaining}d) · ` +
        `lanes TODO ${dist.TODO} / IN PROGRESS ${dist.IN_PROGRESS} / DONE ${dist.DONE} · ` +
        `${blockedCount} blocked`,
    );
  }
  if (progressLines.length > 0) {
    sections.push(`### Sprint progress (active)\n${progressLines.join('\n')}`);
  }

  // --- Recent status changes (active-sprint tasks written in-window) -------
  const changed = activeSprintTasks
    .filter((t) => inWindow(t.updatedAt, fromMs, toMs))
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    .slice(0, PER_SECTION_LIMIT)
    .map((t) => {
      const lane = (t.boardStatus ?? 'TODO').replace('_', ' ');
      const flags = [
        isDone(t) ? 'completed' : '',
        t.blocked ? 'blocked' : '',
        t.adhoc ? 'unplanned' : '',
      ]
        .filter(Boolean)
        .join(', ');
      return `- ${clamp(t.title)} → ${lane}${flags ? ` (${flags})` : ''}`;
    });
  if (changed.length > 0) {
    sections.push(`### Recent status changes\n${changed.join('\n')}`);
  }

  // --- Scope changes: ad-hoc / unplanned tasks pulled in mid-flight --------
  const scope = unplannedScope(activeSprintTasks);
  const scopeAddedInWindow = activeSprintTasks
    .filter((t) => t.adhoc && inWindow(t.addedToSprintAt, fromMs, toMs))
    .sort((a, b) =>
      (b.addedToSprintAt ?? '').localeCompare(a.addedToSprintAt ?? ''),
    )
    .slice(0, PER_SECTION_LIMIT)
    .map((t) => {
      const effort =
        typeof t.effortDays === 'number' && t.effortDays > 0
          ? ` (${t.effortDays}d)`
          : '';
      return `- ${clamp(t.title)}${effort}`;
    });
  if (scope.count > 0 || scopeAddedInWindow.length > 0) {
    const header =
      `Unplanned scope currently in active sprints: ${scope.count} ` +
      `task${scope.count === 1 ? '' : 's'}` +
      (scope.days > 0 ? ` / ${scope.days}d` : '') +
      '.';
    const detail =
      scopeAddedInWindow.length > 0
        ? `\nAdded in this window:\n${scopeAddedInWindow.join('\n')}`
        : '';
    sections.push(`### Scope changes\n${header}${detail}`);
  }

  // --- Standup highlights: task updates created in-window -------------------
  const standup = (taskUpdates ?? [])
    .filter((u) => inWindow(u.createdAt, fromMs, toMs))
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
    .slice(0, PER_SECTION_LIMIT)
    .map((u) => {
      const task = titleById.get(u.taskId) ?? u.taskId;
      const parts: string[] = [];
      if (u.completed) parts.push(`done: ${clamp(u.completed)}`);
      if (u.next) parts.push(`next: ${clamp(u.next)}`);
      if (u.blocker) parts.push(`blocker: ${clamp(u.blocker)}`);
      const author = u.author || 'someone';
      return `- ${author} on “${clamp(task)}” — ${parts.join(' · ') || 'no detail'}`;
    });
  if (standup.length > 0) {
    sections.push(`### Standup highlights\n${standup.join('\n')}`);
  }

  if (sections.length === 0) return '';
  return `SPRINT EXECUTION (this window):\n${sections.join('\n\n')}`;
}
